<template>
  <div>

    <div class="d-flex justify-content-between mb-2">
      <b-checkbox v-model="showAssetNumber">Show Asset Numbers</b-checkbox>
    </div>

    <div class="card mb-3 no-shadow">
      <div class="card-body p-2">
        <div class="d-flex flex-wrap align-items-center">
          <template v-for="(data, rfId) in groupedReleaseForms">
            <div class="cursor-pointer border rounded mr-2 mb-1 p-1"
                 :class="{'bg-light-green': isToggleSelected(rfId)}">

              <div @click="startEditing(rfId)" class="d-flex align-items-center gap-1">
                <div>
                  <div>{{ data.releaseForm.name }}</div>
                  <div class="d-flex font-size-sm gap-1">
                    <div class="font-italic">{{ data.releaseForm.release_type }}</div>
                    <a :href="data.releaseForm.url" target="_blank"><i class="fas fa-external-link"></i></a>
                  </div>
                </div>
                <b-badge v-if="showAssetNumber" variant="primary" title="assets">
                  {{ data.assets.length }}
                </b-badge>
              </div>

            </div>
          </template>

        </div>
      </div>
    </div>

    <!--  Asset with thumbnails / checkboxes list  -->
    <div class="card mb-3 no-shadow">
      <div class="card-body p-2">
        <div class="mb-2 d-flex align-items-start gap-1">
          <b-button size="sm"
                    :variant="editingSelectionSelectAllButtonVariant('all')"
                    @click="editingSelectionSelectAll(true)">
            Select All
          </b-button>

          <b-button size="sm"
                    :variant="editingSelectionSelectAllButtonVariant('none')"
                    @click="editingSelectionSelectAll(false)">
            Unselect All
          </b-button>

          <div class="flex-grow-1"></div>

          <div class="d-flex gap-1 align-items-center" v-if="editingSelectionIds.length > 0">


            <b-select class="w-auto mw-100" :options="operationOptions" v-model="selectedOperation"/>

            <file-input v-if="selectedOperation === 'upload'" v-model="file" class="mr-2" style="max-width: 150px" :multiple="true">
              <template slot="button-slot">
                <b-button variant="success">SELECT FILES</b-button>
              </template>
            </file-input>

            <div class="d-flex align-items-center">
              <b-form-radio-group v-if="selectedOperation === 'upload'"
                                  v-model="releaseFormType"
                                  :options="releaseFormTypeOptions" buttons button-variant="info"/>
            </div>

            <b-select v-if="selectedOperation === 'assign'" class="w-auto mw-100" :options="assignOperationOptions"
                      v-model="selectedAssignReportId"/>

            <b-select v-if="selectedOperation === 'remove'" class="w-auto mw-100" :options="removeOperationOptions"
                      v-model="selectedRemoveReportId"/>

            <div class="d-flex align-items-center">
              <b-form-radio-group v-if="selectedOperation === 'release_required'"
                                  v-model="releaseRequired"
                                  :options="releaseRequiredOptions" buttons button-variant="info"/>
            </div>

            <b-button :variant="selectedOperation === 'remove' ? 'danger' : 'primary'"
                      class="no-wrap"
                      :disabled="bulkActionRunning || editingSelectionIds.length === 0 || !validOperationData"
                      @click="applyToSelection">
              <span v-if="bulkActionRunning">Please wait...</span>
              <span v-else-if="selectedOperation === 'remove'">Remove from Selection</span>
              <span v-else>Apply to Selection</span>
            </b-button>
          </div>
        </div>

        <div class="d-flex flex-wrap align-items-center">
          <div class="cursor-pointer border rounded mr-2 mb-1 p-1" v-for="asset in selectedLoadedAssets">
            <b-checkbox v-model="editingSelection[asset.id]">
              <div class="img-bg img-bg-1 img-square d-flex justify-content-end align-items-end"
                   :style="`background-image: url('${asset.thumb_url}')`">
                <div class="p-1" v-if="!hasReleaseForms(asset)">
                  <b-badge variant="danger">No Form</b-badge>
                  <b-badge variant="danger" v-if="hasReleaseRequiredIssues(asset)" v-b-tooltip title="Asset has no release forms, but is release required"><i class="fas fa-exclamation-triangle"/></b-badge>
                </div>
              </div>
            </b-checkbox>
          </div>
        </div>
      </div>

    </div>

  </div>
</template>

<script>

import GeneralMixins from "../../../../mixins/GeneralMixins";
import PulseLoader from 'vue-spinner/src/PulseLoader';
import normalize from "json-api-normalize";
import Attributes from "../../../../models/Attributes.js";
import FileInput from "../../../Common/FileInput.vue";
import {objectToFormData} from "object-to-formdata";

export default {
  props: ['selectedAssets', 'idKey'],
  components: {FileInput, PulseLoader},
  mixins: [GeneralMixins],

  data() {
    return {
      idKeyName: this.idKey || "id",
      editingSelection: {},
      actionModal: false,
      actionType: null,
      showAssetNumber: true,
      bulkActionRunning: false,
      loaded: false,
      selectedLoadedAssets: [],
      file: null,
      releaseFormType: 'model_release',
      selectedOperation: 'upload',
      selectedAssignReportId: null,
      selectedRemoveReportId: null,
      releaseRequired: false
    }
  },

  mounted() {

    if (this.idKeyName === "id") {
      this.loaded = true
      this.selectedLoadedAssets = this.selectedAssets
    } else {
      const assetIds = this.selectedAssets.map(el => el[this.idKeyName])

      this.$http.post("/curators/bulk/asset_loader", {asset_ids: assetIds}).then(result => {
        this.selectedLoadedAssets = normalize(result.body).get(Attributes.assetWithIncludesAttributes);
      }, rejectionResult => {
        this.showErrorToast(null, rejectionResult.body.errors)
      })
    }
  },

  watch: {
    selectedOperation(val) {
      if (val === 'remove') {
        if (this.removeOperationOptions.length > 0) {
          this.selectedRemoveReportId = this.removeOperationOptions[0].value
        }
      } else if (val === 'assign') {
        if (this.assignOperationOptions.length > 0) {
          this.selectedAssignReportId = this.assignOperationOptions[0].value
        }
      }
    }
  },

  methods: {

    hasReleaseForms(asset) {
      return asset.release_forms && asset.release_forms.length > 0
    },

    hasReleaseRequiredIssues(asset) {
      return asset.release_required === true && !this.hasReleaseForms(asset)
    },

    startEditing(rfId) {
      this.editingSelection = Object.assign({}, ...this.selectedLoadedAssets.map(asset => {
        return {
          [asset.id]: asset.release_forms && asset.release_forms.map(rf => rf.id).includes(rfId)
        }
      }))
    },

    isToggleSelected(rfId) {
      const assetIds = this.groupedReleaseForms[rfId].assets.map(a => a.id)
      if (assetIds.length === 0 || assetIds.length !== this.editingSelectionIds.length) {
        return false
      }

      let set = [...new Set([...this.editingSelectionIds, ...assetIds])]
      return set.length === assetIds.length && assetIds.length === this.editingSelectionIds.length;
    },

    startUploadOperation() {
      const formData = objectToFormData({
        asset_ids: this.editingSelectionIds,
        data: this.file,
        release_type: this.releaseFormType
      }, {nullsAsUndefineds: true}, new FormData());

      this.startOperation("/curators/bulk/release_forms/upload", formData, () => {
        this.file = null
      })
    },

    startAssignOperation() {
      this.startOperation("/curators/bulk/release_forms/assign", {
        asset_ids: this.editingSelectionIds,
        release_form_id: this.selectedAssignReportId
      }, () => {
        this.selectedAssignReportId = null
      })
    },

    startRemoveOperation() {
      this.startOperation("/curators/bulk/release_forms/remove", {
        asset_ids: this.editingSelectionIds,
        release_form_id: this.selectedRemoveReportId
      }, () => {
        this.selectedRemoveReportId = null
      })
    },

    startReleaseRequiredOperation() {
      this.startOperation("/curators/bulk/release_forms/release_required", {
        asset_ids: this.editingSelectionIds,
        release_required: this.releaseRequired
      })
    },

    startOperation(endpoint, params, onSuccess) {
      this.bulkActionRunning = true

      return this.$http.post(endpoint, params).then(result => {
        let assets = normalize(result.body).get(Attributes.assetWithIncludesAttributes);
        let count = assets.length

        const message = result.body.message || `${count} assets updated`
        this.showToast(message, "Success")

        this.selectedLoadedAssets = this.selectedLoadedAssets.map(oldAsset => {
          return assets.find(a => a.id === oldAsset.id) || oldAsset
        })

        if (onSuccess) { onSuccess() }

      }, rejectionResult => {
        this.showErrorToast(null, rejectionResult.body.errors)
      }).then(result => {
        this.bulkActionRunning = false
      });
    },

    applyToSelection() {
      if (!this.validOperationData) { return }
      if (this.selectedOperation === 'upload') {
        this.startUploadOperation()
      } else if (this.selectedOperation === 'assign') {
        this.startAssignOperation()
      } else if (this.selectedOperation === 'remove') {
        this.startRemoveOperation()
      } else if (this.selectedOperation === 'release_required') {
        this.startReleaseRequiredOperation()
      }
    },

    editingSelectionSelectAllButtonVariant(type) {
      if (type === 'all') {
        if (this.editingSelectionAllSelected) {
          return "primary"
        } else {
          return "outline-secondary"
        }
      } else {
        if (this.editingSelectionNoneSelected) {
          return "primary"
        } else {
          return "outline-secondary"
        }
      }
    },

    editingSelectionSelectAll(value) {
      this.editingSelection = Object.assign({}, ...this.selectedLoadedAssets.map(asset => {
        return {
          [asset.id]: value
        }
      }))
    },

    groupReleaseForms(releaseFormsIds, assets) {
      return releaseFormsIds.reduce((obj, releaseFormsId) => {
        obj[releaseFormsId] = {
          assets: assets.filter(el => el.release_forms && el.release_forms.map(rf => rf.id).includes(releaseFormsId)),
          releaseForm: this.findReleaseForm(releaseFormsId)
        }
        return obj
      }, {})
    },

    findReleaseForm(releaseFormId) {
      return this.allUniqueReleaseForms.find(rf => rf.id === releaseFormId)
    },

    uniqueReleaseForms(assets) {
      const rfArrays = assets.map(el => el.release_forms).filter(el => el)
      const rfList = [].concat.apply([], rfArrays)
      return [...new Map(rfList.map(item => [item.id, item])).values()]
    },

  },

  computed: {
    releaseFormTypeOptions() {
      return [
        {text: 'Model', value: 'model_release'},
        {text: 'Property', value: 'property_release'}
      ]
    },

    releaseRequiredOptions() {
      return [
        {text: "Required", value: true},
        {text: "Not Required", value: false}
      ]
    },

    validOperationData() {
      if (!this.selectedOperation || this.editingSelectionIds.length === 0) {
        return false
      }
      if (this.selectedOperation === 'upload') {
        if (!this.file) {
          return false
        }
      } else if (this.selectedOperation === 'assign') {
        if (!this.selectedAssignReportId) {
          return false
        }
      } else if (this.selectedOperation === 'remove') {
        if (!this.selectedRemoveReportId) {
          return false
        }
      }

      return true
    },

    operationOptions() {
      return [
        {text: 'Upload New', value: 'upload'},
        {text: 'Assign Existing', value: 'assign'},
        {text: 'Remove', value: 'remove'},
        {text: 'Release Required?', value: 'release_required'},
      ]
    },

    assignOperationOptions() {
      return this.allUniqueReleaseForms.map(el => {
        return {text: el.name, value: el.id}
      })
    },

    removeOperationOptions() {
      return this.uniqueReleaseForms(this.currentAssetSelection).map(el => {
        return {text: el.name, value: el.id}
      })
    },

    editingSelectionAllSelected() {
      if (!this.editingSelection) {
        return false
      }

      return this.editingSelectionIds.length === this.selectedLoadedAssets.length
    },

    allUniqueReleaseFormsIds() {
      return this.allUniqueReleaseForms.map(el => el.id)
    },

    allUniqueReleaseForms() {
      return this.uniqueReleaseForms(this.selectedLoadedAssets)
    },

    editingSelectionNoneSelected() {
      if (!this.editingSelection) {
        return false
      }
      return this.editingSelectionIds.length === 0
    },

    groupedReleaseForms() {
      return this.groupReleaseForms(this.allUniqueReleaseFormsIds, this.selectedLoadedAssets)
    },

    currentAssetSelection() {
      const assetIds = Object.keys(this.editingSelection).filter(k => this.editingSelection[k])
      return this.selectedLoadedAssets.filter(asset => assetIds.includes(asset.id))
    },

    editingSelectionIds() {
      if (!this.editingSelection) {
        return []
      }

      return Object.keys(this.editingSelection).filter(id => this.editingSelection[id])
    },

    modalActionDisabled() {
      return this.editingSelectionIds.length === 0
    },
  }
}
</script>
